// suprimentos.service.ts
//
// O caminho pelo qual as telas de Suprimentos passam a ler dado.
//
// O que havia antes: `DynamicTable` lia TUDO do JWT — `supplierInfo`,
// `replenishmentLimitInfo`, `movementTypeInfo` e `bomInfo`. Um `grep` dessas
// quatro chaves no backend inteiro devolvia ZERO ocorrências: elas nunca foram
// produzidas por ninguém. O `|| []` de cada tela engolia isso em silêncio, e a
// tabela ficava permanentemente vazia parecendo "ainda não cadastrado".
//
// Agora o dado vem de REST paginado, com FILTRO NO SERVIDOR. O InfoToken volta
// a ser o que devia: identidade, escopo e permissão.
//
// MASCARAMENTO: preço acordado, preço fixo e teto de linha podem NÃO VIR na
// resposta, conforme o papel de quem pediu. Isso é decidido no servidor — a
// coluna sai de fora do JSON, não fica escondida na tela. Uma chave ausente
// aqui não é erro: é a resposta certa para quem não alcança dinheiro.

import api from "./api.service";

export type AgregadoDeSuprimentos =
  | "fornecedores"
  | "precos-acordados"
  | "locais-de-estoque"
  | "tipos-de-movimento"
  | "limites-de-reposicao"
  | "fichas-tecnicas"
  | "categorias"
  | "grupos";

export interface Paginacao {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface RespostaPaginada<T = any> {
  dados: T[];
  paginacao: Paginacao;
}

export interface FiltroDeListagem {
  page?: number;
  limit?: number;
  busca?: string;
  status?: "ativo" | "inativo";
  subsidiary?: string;
  organizacao?: string;
}

const suprimentosService = {
  async listar<T = any>(
    agregado: AgregadoDeSuprimentos,
    filtro: FiltroDeListagem = {},
  ): Promise<RespostaPaginada<T>> {
    const parametros = new URLSearchParams();
    // Só o que foi preenchido vai na query: mandar `busca=` vazio faria o
    // servidor montar um ILIKE '%%' à toa em toda troca de página.
    if (filtro.page) parametros.set("page", String(filtro.page));
    if (filtro.limit) parametros.set("limit", String(filtro.limit));
    if (filtro.busca) parametros.set("busca", filtro.busca);
    if (filtro.status) parametros.set("status", filtro.status);
    if (filtro.subsidiary) parametros.set("subsidiary", filtro.subsidiary);
    if (filtro.organizacao) parametros.set("organizacao", filtro.organizacao);

    const { data } = await api.get(
      `suprimentos/${agregado}?${parametros.toString()}`,
    );
    return data;
  },

  async criar(agregado: AgregadoDeSuprimentos, corpo: any) {
    const { data } = await api.post(`suprimentos/${agregado}`, corpo);
    return data;
  },

  async atualizar(agregado: AgregadoDeSuprimentos, uuid: string, corpo: any) {
    const { data } = await api.patch(`suprimentos/${agregado}/${uuid}`, corpo);
    return data;
  },

  /**
   * DESLIGA, não apaga.
   *
   * Fornecedor, limite e ficha aparecem em histórico de compra: um DELETE real
   * levaria junto a explicação de por que uma ordem antiga saiu daquele jeito.
   * O nome do método diz o que ele faz.
   */
  async desligar(
    agregado: AgregadoDeSuprimentos,
    uuid: string,
    updatedBy: string,
  ) {
    const { data } = await api.delete(`suprimentos/${agregado}/${uuid}`, {
      data: { updatedBy },
    });
    return data;
  },

  async precosDoFornecedor(fornecedorUuid: string) {
    const { data } = await api.get(
      `suprimentos/fornecedores/${fornecedorUuid}/precos`,
    );
    return data?.dados ?? [];
  },
};

export default suprimentosService;
